'use client';

import { useMemo } from 'react';
import type { Country } from '../../constants';
import type { FormattedShippingSLA } from '../../utils/date/formatShippingSLA';
import { formatShippingSLA } from '../../utils/date/formatShippingSLA';
import type { ShippingPolicyItem } from '../../model/siteconfig/siteconfig';
import { useSiteconfigService } from './useSiteconfigService';

export interface UseShippingSLAResult {
  /** Display-ready strings derived from the policy — undefined when no data */
  formatted: FormattedShippingSLA | undefined;
  /** Full policy entry so callers retain access to shippingDuration / freeShippingThreshold */
  shippingPolicy: ShippingPolicyItem | undefined;
  /** Convenience flag: a display-ready SLA block is available (Tier 1 or Tier 2) */
  hasSLA: boolean;
}

/**
 * Dynamic SLA hook (SSD-3986 Phase 2) — Suspense-backed.
 *
 * Suspends the caller until `/shippingPolicies` resolves, so UIs can render a
 * skeleton via an ancestor `<Suspense>` / `QueryBoundary` boundary. Tier 1
 * (`dynamicSla.p70Date` + `p90Date`) uses measured P70/P90 dates; Tier 2 falls
 * back to `deliveryDateRange`. The tier is surfaced on `formatted.tier` for
 * tests / analytics only — the visible block looks the same either way.
 */
export const useShippingSLA = (country: Country): UseShippingSLAResult => {
  const { useGetShippingPoliciesSuspenseQuery } = useSiteconfigService();
  const { data: shippingPolicies } = useGetShippingPoliciesSuspenseQuery();

  const shippingPolicy = useMemo(
    () => shippingPolicies?.config.find((policy) => policy.countryCode === country),
    [shippingPolicies, country],
  );

  const formatted = useMemo(() => formatShippingSLA(shippingPolicy), [shippingPolicy]);

  return {
    formatted,
    shippingPolicy,
    hasSLA: Boolean(formatted),
  };
};
